import * as React from 'react';
import styled from 'styled-components';
import Arrow from './icon/arrow.png';
import { Dialog, DialogCloseEvent } from '@progress/kendo-react-dialogs';

type props = {
  onClose?: (event: DialogCloseEvent) => void;
  backNumberData?: string[] | undefined;
  isAbove?: boolean;
  isTryIt?: boolean;
};
const itemsPerPage = 54;
const pagesPerGroup = 10; // Show 10 pages at a time

const BackNumberTableComponent = ({
  onClose,
  backNumberData,
  isAbove,
  isTryIt,
}: props) => {
  // Sort the data in ascending order
  const sortedData = backNumberData
    ?.slice()
    .sort((a, b) => parseInt(a) - parseInt(b));

  const [currentPage, setCurrentPage] = React.useState(1);
  const [currentPageGroup, setCurrentPageGroup] = React.useState(0);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    // Update page group if the new page is outside current group
    const newPageGroup = Math.floor((pageNumber - 1) / pagesPerGroup);
    setCurrentPageGroup(newPageGroup);
  };

  const handleNextGroup = () => {
    setCurrentPageGroup(prev => prev + 1);
    setCurrentPage((currentPageGroup + 1) * pagesPerGroup + 1);
  };

  const handlePrevGroup = () => {
    setCurrentPageGroup(prev => prev - 1);
    setCurrentPage(currentPageGroup * pagesPerGroup - pagesPerGroup + 1);
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const filteredData = isTryIt
    ? sortedData?.filter(item => parseInt(item) < 100)
    : sortedData;
  const displayedData = filteredData?.slice(startIndex, endIndex);
  const totalData = filteredData?.length;
  const totalPages = Math.ceil((filteredData?.length || 0) / itemsPerPage);

  // Calculate pagination range
  const startPage = currentPageGroup * pagesPerGroup + 1;
  const endPage = Math.min(startPage + pagesPerGroup - 1, totalPages);
  const totalGroups = Math.ceil(totalPages / pagesPerGroup);

  const paginationText = `Showing ${startIndex + 1}-${endIndex > (displayedData?.length || 0)
      ? startIndex + (displayedData?.length || 0)
      : endIndex
    } of ${totalData} items`;

  return (
    <StyledDialog title={'Back Number '} onClose={onClose}>
      <MainWrapper>
        <div className="grid-container">
          {displayedData?.map((item, index) => (
            <div key={index} className="nested-backnumber">
              {item}
            </div>
          ))}
        </div>
        <div className="pagination">
          <div className="pagination-wrapper">
            <div className="pagination-btns">
              {/* Previous page button */}
              <button
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
                className="nav-btn"
              >
                <img src={Arrow} alt="arrow" />
              </button>

              {/* Previous group button */}
              {currentPageGroup > 0 && (
                <button onClick={handlePrevGroup} className="group-nav-btn">
                  ...
                </button>
              )}

              {/* Page numbers for current group */}
              {Array.from({ length: endPage - startPage + 1 }, (_, index) => {
                const pageNumber = startPage + index;
                return (
                  <button
                    key={pageNumber}
                    onClick={() => handlePageChange(pageNumber)}
                    className={currentPage === pageNumber ? 'active' : ''}
                  >
                    {pageNumber}
                  </button>
                );
              })}

              {/* Next group button */}
              {currentPageGroup < totalGroups - 1 && (
                <button onClick={handleNextGroup} className="group-nav-btn">
                  ...
                </button>
              )}

              {/* Next page button */}
              <button
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
                className="nav-btn"
              >
                <img src={Arrow} className="next-arrow" alt="arrow" />
              </button>
            </div>
            <div className="pagination-info">{paginationText}</div>
          </div>
        </div>
      </MainWrapper>
    </StyledDialog>
  );
};

const StyledDialog = styled(Dialog)`
  &.k-dialog-wrapper .k-dialog {
    width: 60%;
    height: 80%;
    max-height: 500px;
    background: #27292d;
    color: #a7a7a7;
    border-radius: 15px;
    border: 1px solid;
    border-color: #707070;
  }
  .k-window-title {
    color: #9597a6;
    font-size: 15px;
    font-weight: 600;
    padding-left: 10px;
    display: flex;
    justify-content: center;
    font-size: 17px;
  }
  p {
    text-align: justify !important;
    margin: 10px !important;
  }
  .k-window-titlebar {
    border-bottom-color: transparent;
    padding: 5px;
    height: 10%;
  }
  &.k-dialog-wrapper {
    backdrop-filter: blur(10px) !important;
  }
  .k-window-content,
  .k-prompt-container {
    padding: 0px;
    overflow: hidden;
  }
  .pagination {
    padding: 10px 20px;
    display: grid;
    align-items: center;
    height: auto;
    min-height: 60px;
  }
  .pagination-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }
  .pagination-btns {
    gap: 4px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
  }
  button {
    background: transparent;
    border: unset;
    color: white;
    border-radius: 5px;
    min-width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }
  .nav-btn img {
    width: 16px;
    height: 16px;
  }
  .next-arrow {
    transform: rotate(180deg);
  }
  .active {
    background-color: #306fdb;
  }
  .group-nav-btn {
    color: #a7a7a7;
    font-weight: bold;
  }
  @media (min-width: 100px) and (max-width: 319px) {
    &.k-dialog-wrapper .k-dialog {
      width: 95%;
      height: 85%;
    }
    .k-window-title {
      font-size: 11px;
    }
    .pagination-btns {
      gap: 2px;
    }
    button {
      min-width: 20px;
      height: 20px;
      font-size: 8px;
    }
  }
  @media (min-width: 320px) and (max-width: 575px) {
    &.k-dialog-wrapper .k-dialog {
      width: 90%;
      height: 85%;
    }
    .k-window-title {
      font-size: 12px;
    }
    .pagination-btns {
      gap: 3px;
    }
    button {
      min-width: 22px;
      height: 22px;
      font-size: 9px;
    }
  }
  @media (min-width: 576px) and (max-width: 767px) {
    &.k-dialog-wrapper .k-dialog {
      width: 80%;
      height: 85%;
    }
    .k-window-title {
      font-size: 13px;
    }
    .pagination-btns {
      gap: 4px;
    }
    button {
      min-width: 25px;
      height: 25px;
      font-size: 10px;
    }
  }
  @media (min-width: 768px) and (max-width: 991px) {
    &.k-dialog-wrapper .k-dialog {
      width: 70%;
      height: 85%;
    }
    .k-window-title {
      font-size: 15px;
    }
  }
  @media (min-width: 992px) and (max-width: 1199px) {
    &.k-dialog-wrapper .k-dialog {
      width: 65%;
      height: 85%;
    }
    .k-window-title {
      font-size: 16px;
    }
  }
  @media (min-width: 1200px) and (max-width: 1365px) {
    &.k-dialog-wrapper .k-dialog {
      width: 60%;
      height: 85%;
    }
    .k-window-title {
      font-size: 17px;
    }
  }
  @media (min-width: 1366px) and (max-width: 1448px) {
    &.k-dialog-wrapper .k-dialog {
      width: 50%;
      height: 85%;
    }
    .k-window-title {
      font-size: 18px;
    }
  }
  @media (min-width: 1449px) and (max-width: 1719px) {
    &.k-dialog-wrapper .k-dialog {
      width: 50%;
      height: 85%;
    }
    .k-window-title {
      font-size: 19px;
    }
  }
  @media (min-width: 1720px) and (max-width: 4000px) {
    &.k-dialog-wrapper .k-dialog {
      width: 50%;
      height: 85%;
      max-height: 600px;
    }
    .k-window-titlebar {
      border-bottom-color: transparent;
      padding: 5px;
      height: 8%;
    }
    .k-window-title {
      font-size: 21px;
    }
  }
`;

const MainWrapper = styled.div`
  border-top: 1px solid #707070;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
  overflow: hidden;
  .team-id {
    max-width: 40px;
    border-right: 1px solid #707070;
  }
  .nested-backnumber {
    color: #306fdb;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 5px;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .k-window-content {
    padding: 0px !important;
    border-top: 1px solid #707070;
    border-bottom: 1px solid #707070;
  }
  .grid-container > div:last-child {
    border-bottom: none;
  }
  .grid-container {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-auto-rows: 40px;
    position: relative;
    font-size: 12px;
    border-top: 1px solid #707070;
    border-bottom: 1px solid #707070;
    border-color: #707070;
    border: 1px solid #707070;
    width: 100%;
    height: auto;
    max-height: calc(100vh - 200px);
    overflow-y: auto;
    overflow-x: hidden;
    ::-webkit-scrollbar {
      width: 5px;
      margin-left: 3px;
    }
    ::-webkit-scrollbar-track {
      background: transparent !important;
      border-radius: 10px;
    }
    ::-webkit-scrollbar-thumb {
      width: 9px;
      background: #707070 !important;
      border-radius: 10px;
    }
  }

  .grid-container > div {
    background-color: #27292d;
    width: 100%;
    text-align: center;
    font-size: 12px;
    border-right: 1px solid #707070;
    border-bottom: 1px solid #707070;
    height: 40px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .grid-container > div:nth-child(3n) {
    border-right: none;
  }
  .column-nested {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
  }
  .column-nested > div {
    flex: 0 0 100%;
    padding: 5px 8px;
    text-align: center;
  }

  .column-1,
  .column-2,
  .column-3,
  .column-4,
  .column-5,
  .column-6,
  .column-7,
  .column-8,
  .column-9,
  .column-10,
  .column-11,
  .column-12,
  .column-13,
  .column-14,
  .column-15,
  .column-16,
  .column-17,
  .column-18 {
    display: contents;
  }

  @media (min-width: 100px) and (max-width: 319px) {
    .team-id {
      max-width: 68px;
    }
    .column-nested > div {
      font-size: 8px;
      padding: 2px 3px;
    }
    .nested-backnumber {
      text-align: center;
    }
    .pagination {
      padding: 8px 3px;
      height: auto;
      min-height: 50px;
    }
    .pagination-wrapper {
      flex-direction: column;
      gap: 5px;
    }
    .pagination-info {
      font-size: 8px;
      padding: 0;
      width: 100%;
      text-align: center;
    }
  }
  @media (min-width: 320px) and (max-width: 575px) {
    .team-id {
      max-width: 68px;
    }
    .column-nested > div {
      font-size: 9px;
      padding: 2px 4px;
    }
    .pagination {
      padding: 8px 3px;
      height: auto;
      min-height: 50px;
    }
    .pagination-wrapper {
      flex-direction: column;
      gap: 5px;
    }
    .pagination-info {
      font-size: 9px;
      padding: 0;
      width: 100%;
      text-align: center;
    }
  }
  @media (min-width: 576px) and (max-width: 767px) {
    .team-id {
      max-width: 68px;
    }
    .column-nested > div {
      font-size: 10px;
      padding: 2px 4px;
    }
    .pagination {
      padding: 8px 3px;
      height: auto;
      min-height: 50px;
    }
    .pagination-wrapper {
      flex-direction: column;
      gap: 5px;
    }
    .pagination-info {
      font-size: 10px;
      padding: 0;
      width: 100%;
      text-align: center;
    }
  }
  @media (min-width: 768px) and (max-width: 991px) {
    .team-id {
      max-width: 68px;
    }
    .column-nested > div {
      font-size: 11px;
      padding: 2px 5px;
    }
    .pagination {
      padding: 10px 5px;
      height: auto;
      min-height: 60px;
    }
    .pagination-info {
      font-size: 11px;
      padding: 0 2px;
    }
  }
  @media (min-width: 992px) and (max-width: 1199px) {
    .team-id {
      max-width: 68px;
    }
    .column-nested > div {
      font-size: 12px;
      padding: 4px 5px;
    }
    .pagination {
      padding: 10px 5px;
      height: 60px;
    }
    .pagination-info {
      font-size: 12px;
      padding: 0 2px;
    }
  }
  @media (min-width: 1200px) and (max-width: 1365px) {
    .team-id {
      max-width: 68px;
    }
    .column-nested > div {
      font-size: 13px;
      padding: 5px 15px;
    }
    .pagination {
      padding: 10px 5px;
      height: 60px;
    }
    .pagination-info {
      font-size: 13px;
      padding: 0 2px;
    }
    .pagination-wrapper {
      padding: 10px 0px;
    }
  }
  @media (min-width: 1366px) and (max-width: 1448px) {
    .team-id {
      max-width: 68px;
    }
    .column-nested > div {
      font-size: 14px;
      padding: 5px 15px 0px;
    }
    .pagination {
      padding: 10px 5px;
      height: 60px;
    }
    .pagination-info {
      font-size: 14px;
      padding: 0 5px;
    }
    .pagination-wrapper {
      padding: 10px 0px;
    }
  }
  @media (min-width: 1449px) and (max-width: 1719px) {
    .team-id {
      max-width: 68px;
    }
    .column-nested > div {
      font-size: 17px;
      padding: 5px 15px 0px;
    }
    .pagination {
      padding: 10px 15px;
      height: 60px;
    }
    .pagination-info {
      font-size: 17px;
      padding: 0 5px;
    }
    .pagination-wrapper {
      padding: 10px 0px;
    }
  }
  @media (min-width: 1720px) and (max-width: 4000px) {
    .team-id {
      max-width: 68px;
    }
    .column-nested > div {
      font-size: 18px;
      padding: 5px 15px 0px;
    }
    .pagination {
      padding: 10px 15px;
      height: 60px;
    }
    .pagination-info {
      font-size: 18px;
      padding: 0 5px;
    }
    .pagination-wrapper {
      padding: 10px 0px;
    }
  }
`;

export default BackNumberTableComponent;
